import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { excelImportService } from '../services/excelImport.service';
import { storageRoomService } from '../services/storageRoom.service';
import { useAuth, isAdminUser } from './useAuth';
import { useAdminStorageViewAll } from './useAdminStorageViewAll';
import { deliverBlobToUser } from '../utils/downloadBlob';
import {
  getAxiosOrBlobErrorMessage,
  translateAxiosErrorMessage,
} from '../utils/error.utils';

const IMPORT_FILE_RE = /\.(xlsx|xls|csv)$/i;

export function isExcelImportFile(file: File): boolean {
  return IMPORT_FILE_RE.test(file.name);
}

/** Excel 入出力ページの状態・mutation・ハンドラ */
export function useExcelImportFlow(t: TFunction) {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedStorageRoomId, setSelectedStorageRoomId] = useState<number | undefined>(
    undefined
  );
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dropActive, setDropActive] = useState(false);

  const {
    data: storageRoomsData,
    isPending: storageRoomsPending,
    isError: storageRoomsError,
  } = useQuery({
    queryKey: ['storageRooms', 'excel-import', adminViewAll],
    queryFn: () =>
      storageRoomService.list({
        page: 1,
        limit: 1000,
        adminViewAll: adminViewAll || undefined,
      }),
  });

  const storageRooms = storageRoomsData?.data ?? [];

  useEffect(() => {
    if (selectedStorageRoomId != null || storageRooms.length !== 1) return;
    setSelectedStorageRoomId(storageRooms[0].id);
  }, [storageRooms, selectedStorageRoomId]);

  useEffect(() => {
    if (selectedStorageRoomId == null) return;
    if (storageRooms.some((room) => room.id === selectedStorageRoomId)) return;
    setSelectedStorageRoomId(undefined);
  }, [storageRooms, selectedStorageRoomId]);
  const downloadTemplateMutation = useMutation({
    mutationFn: ({
      storageRoomId,
      format,
    }: {
      storageRoomId?: number;
      format?: 'excel' | 'csv';
    }) => excelImportService.downloadTemplate(storageRoomId, format || 'excel'),
    onSuccess: (blob, variables) => {
      const filename =
        variables.format === 'csv' ? 'sample_import_template.csv' : 'sample_import_template.xlsx';
      deliverBlobToUser(blob, filename);
      setStep(2);
    },
    onError: async (err: unknown) => {
      setError(
        await getAxiosOrBlobErrorMessage(
          err,
          t('excel_import.template_download_failed', {
            defaultValue: 'Failed to download template.',
          })
        )
      );
    },
  });

  const validateMutation = useMutation({
    mutationFn: (file: File) => excelImportService.validateFile(file),
    onSuccess: (result) => {
      if (result.success && result.data.errors.length === 0 && result.data.duplicates.length === 0) {
        setStep(3);
      } else {
        setError(
          t('excel_import.validation_errors', {
            defaultValue:
              'Validation: {{errorCount}} error(s), {{duplicateCount}} duplicate(s) found',
            errorCount: result.data.errors.length,
            duplicateCount: result.data.duplicates.length,
          })
        );
      }
    },
    onError: (err: unknown) => {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('excel_import.validation_failed', { defaultValue: 'File validation failed.' })
        )
      );
    },
  });

  const importMutation = useMutation({
    mutationFn: ({ file, storageRoomId }: { file: File; storageRoomId?: number }) =>
      excelImportService.importFile(file, storageRoomId),
    onSuccess: (result) => {
      setSuccess(
        result.message ||
          t('excel_import.import_success', {
            defaultValue: 'Imported {{count}} row(s).',
            count: result.data.imported,
          })
      );
      setStep(4);
      setSelectedFile(null);
    },
    onError: (err: unknown) => {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('excel_import.import_failed', { defaultValue: 'Import failed.' })
        )
      );
    },
  });

  const exportDataMutation = useMutation({
    mutationFn: (storageRoomId: number) => excelImportService.exportData(storageRoomId),
    onSuccess: (blob) => {
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      deliverBlobToUser(blob, `sample_export_${timestamp}.xlsx`);
      setSuccess(
        t('excel_import.export_success', { defaultValue: 'Data exported successfully.' })
      );
    },
    onError: async (err: unknown) => {
      setError(
        await getAxiosOrBlobErrorMessage(
          err,
          t('excel_import.export_failed', { defaultValue: 'Export failed.' })
        )
      );
    },
  });

  const acceptImportFile = useCallback(
    (file: File | undefined | null) => {
      if (!file) return;
      if (!isExcelImportFile(file)) {
        setError(
          t('excel_import.invalid_file_type', {
            defaultValue: 'Only Excel (.xlsx, .xls) or CSV (.csv) files can be uploaded.',
          })
        );
        return;
      }
      setSelectedFile(file);
      setError('');
    },
    [t]
  );

  const resetImportFlow = () => {
    setStep(1);
    setSelectedFile(null);
    setSelectedStorageRoomId(undefined);
    setSuccess('');
    setError('');
    setDropActive(false);
  };

  return {
    showAdminViewToggle,
    adminViewAll,
    setAdminViewAll,
    step,
    setStep,
    error,
    setError,
    success,
    setSuccess,
    dropActive,
    setDropActive,
    selectedFile,
    selectedStorageRoomId,
    setSelectedStorageRoomId,
    storageRooms,
    storageRoomsPending,
    storageRoomsError,
    downloadTemplateMutation,
    validateMutation,
    importMutation,
    exportDataMutation,
    acceptImportFile,
    resetImportFlow,
    handleDownloadTemplate: (format: 'excel' | 'csv' = 'excel') => {
      downloadTemplateMutation.mutate({ storageRoomId: selectedStorageRoomId, format });
    },
    handleFileSelect: (e: ChangeEvent<HTMLInputElement>) => {
      acceptImportFile(e.target.files?.[0]);
    },
    goToUploadStep: () => {
      if (!selectedStorageRoomId) return;
      setError('');
      setStep(2);
    },
    handleValidate: () => {
      if (selectedFile) validateMutation.mutate(selectedFile);
    },
    handleImport: () => {
      if (selectedFile) {
        importMutation.mutate({ file: selectedFile, storageRoomId: selectedStorageRoomId });
      }
    },
  };
}
